import type {
  ExternalToolResultsMessage,
  TerminalOutputItem,
  TerminalOutputMessage,
  UserConfirmationMessage,
} from './confirmation.messages.types';
import type { PortalToolOutput } from './portal-tools/portal-tool-output-registry';

export const TERMINAL_EXECUTION_TOOL_NAME = 'terminal-execution-tool';
export const RUN_COMMAND_TOOL_NAME = 'run_command';
export const RUN_BACKGROUND_TOOL_NAME = 'run_background';
export const READ_JOB_OUTPUT_TOOL_NAME = 'read_job_output';
export const KILL_JOB_TOOL_NAME = 'kill_job';
export const INTERACTIVE_SESSION_TOOL_NAME = 'interactive_session';

export const TERMINAL_TOOL_NAMES = [
  TERMINAL_EXECUTION_TOOL_NAME,
  RUN_COMMAND_TOOL_NAME,
  RUN_BACKGROUND_TOOL_NAME,
  READ_JOB_OUTPUT_TOOL_NAME,
  KILL_JOB_TOOL_NAME,
  INTERACTIVE_SESSION_TOOL_NAME,
] as const;

export type TerminalToolName = (typeof TERMINAL_TOOL_NAMES)[number];

const TERMINAL_TOOL_NAME_SET: ReadonlySet<string> = new Set(
  TERMINAL_TOOL_NAMES,
);

export const isTerminalToolName = (
  toolName: string | undefined | null,
): toolName is TerminalToolName =>
  typeof toolName === 'string' && TERMINAL_TOOL_NAME_SET.has(toolName);

export type TerminalToolPresentation = {
  name: string;
  runningText: string;
  labelPast: string;
  inProgressVerb: string;
  completedVerb: string;
};

const SHELL_COMMAND_PRESENTATION: TerminalToolPresentation = {
  name: 'Shell',
  runningText: 'Running shell command...',
  labelPast: 'Ran shell command',
  inProgressVerb: 'Running',
  completedVerb: 'Ran',
};

export const TERMINAL_TOOL_PRESENTATION: Record<
  TerminalToolName,
  TerminalToolPresentation
> = {
  [TERMINAL_EXECUTION_TOOL_NAME]: SHELL_COMMAND_PRESENTATION,
  [RUN_COMMAND_TOOL_NAME]: SHELL_COMMAND_PRESENTATION,
  [RUN_BACKGROUND_TOOL_NAME]: {
    name: 'Background job',
    runningText: 'Starting background job...',
    labelPast: 'Started background job',
    inProgressVerb: 'Starting',
    completedVerb: 'Started',
  },
  [READ_JOB_OUTPUT_TOOL_NAME]: {
    name: 'Job output',
    runningText: 'Reading job output...',
    labelPast: 'Read job output',
    inProgressVerb: 'Reading',
    completedVerb: 'Read',
  },
  [KILL_JOB_TOOL_NAME]: {
    name: 'Stop job',
    runningText: 'Stopping job...',
    labelPast: 'Stopped job',
    inProgressVerb: 'Stopping',
    completedVerb: 'Stopped',
  },
  [INTERACTIVE_SESSION_TOOL_NAME]: {
    name: 'Interactive session',
    runningText: 'Driving interactive session...',
    labelPast: 'Drove interactive session',
    inProgressVerb: 'Driving',
    completedVerb: 'Drove',
  },
};
export const ASK_USER_TOOL_NAME = 'ask-user';
export const ASK_USER_SKIP_SENTINEL = '__ask_user_skip__';

export type UserConfirmationPayload =
  | UserConfirmationMessage
  | TerminalOutputMessage
  | ExternalToolResultsMessage;

export type ExternalToolBatchEntry = {
  batchId: string;
  chatId?: string;
  resumeGroupId?: string;
  toolCallId: string;
  toolName: string;
};

export type ExternalToolPendingInterrupt = {
  id: string;
  toolCallId: string;
  toolName: string;
  batchId: string;
  chatId?: string;
  resumeGroupId?: string;
};

export type TerminalExternalToolResultItem = {
  batchId?: string;
  toolCallId: string;
  toolName: TerminalToolName;
  status: 'success' | 'error';
  result: TerminalOutputItem;
};

export type ExternalToolResumeValue =
  | TerminalOutputItem
  | ExternalToolResultsMessage;

export type ExternalToolResumeMap = Record<string, ExternalToolResumeValue>;

export type PortalToolHandlerResultPayload =
  | PortalToolOutput
  | TerminalOutputItem;

export type ExternalToolResult = {
  batchId: string;
  chatId: string;
  toolCallId: string;
  toolName: string;
  status: 'success' | 'error';
  result: PortalToolHandlerResultPayload;
};
